import { useState, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'study-detail-split-ratio';
const DEFAULT_LEFT_PERCENT = 58;
const MIN_VIEWER_PX = 600; /* keep in sync with --viewer-min-width in variables.css */
const MIN_REPORT_PX = 520;

function getSavedSizes(): [number, number] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as unknown;
      if (
        Array.isArray(parsed) &&
        parsed.length === 2 &&
        typeof parsed[0] === 'number' &&
        typeof parsed[1] === 'number' &&
        parsed[0] >= 20 &&
        parsed[0] <= 85
      ) {
        return [parsed[0], parsed[1]];
      }
    }
  } catch {
    /* localStorage unavailable or invalid */
  }
  return [DEFAULT_LEFT_PERCENT, 100 - DEFAULT_LEFT_PERCENT];
}

function saveSizes(sizes: number[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes));
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * Persisted split sizes for the Study Detail viewer/report layout.
 * Uses react-split (Split.js) which owns the gutter and drag behavior.
 * The viewer grid resizes itself during drag via a ResizeObserver on the grid
 * container (see useViewportResize), so no per-drag callback is needed here.
 */
export function useResizableLayout() {
  const [sizes, setSizes] = useState<[number, number]>(getSavedSizes);

  const handleDragEnd = useCallback((newSizes: number[]) => {
    const left = Math.round(newSizes[0]);
    const right = 100 - left;
    setSizes([left, right]);
    saveSizes([left, right]);
  }, []);

  const gutterStyle = useCallback(
    (_dimension: string, gutterSize: number) => ({
      background: 'var(--bg-elevated)',
      cursor: 'col-resize',
      width: `${gutterSize}px`,
      borderLeft: '1px solid var(--border)',
      borderRight: '1px solid var(--border)',
    }),
    []
  );

  const splitProps = useMemo(
    () => ({
      sizes,
      minSize: [MIN_VIEWER_PX, MIN_REPORT_PX] as [number, number],
      gutterSize: 6,
      snapOffset: 0,
      direction: 'horizontal' as const,
      cursor: 'col-resize' as const,
      gutterStyle,
      onDragEnd: handleDragEnd,
    }),
    [sizes, gutterStyle, handleDragEnd]
  );

  return { splitProps };
}
